import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchExecutiveDashboard } from '@/store/slices/dashboardSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import { fetchScenarios } from '@/store/slices/scenariosSlice';
import { formatCurrencyCompact, formatPercentTrend } from '@/utils/formatters';

// Components
import KPICards from './components/KPICards';
import CategoryComplianceChart from './components/CategoryComplianceChart';
import QuarterlyReadinessChart from './components/QuarterlyReadinessChart';
import InvestmentByISChart from './components/InvestmentByISChart';
import DependenciesAtRiskList from './components/DependenciesAtRiskList';
import EpicDeliveryTrendChart from './components/EpicDeliveryTrendChart';

// Design System
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  SectionReveal,
} from '@/design-system';
import { BarChart3, Download, FileText } from 'lucide-react';

const ExecutiveDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { executive } = useSelector((state: RootState) => state.dashboard);
  const { periods: planningPeriods } = useSelector((state: RootState) => state.planningPeriods);
  const { scenarios } = useSelector((state: RootState) => state.scenarios);

  const isLoading = executive?.isLoading || false;

  const [selectedPlanningPeriod, setSelectedPlanningPeriod] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      dispatch(fetchScenarios({ planningPeriodId: selectedPlanningPeriod }));
    }
  }, [dispatch, selectedPlanningPeriod]);

  useEffect(() => {
    if (planningPeriods.length > 0 && !selectedPlanningPeriod) {
      const activePeriod = planningPeriods.find((p: any) => p.isActive);
      const periodId = activePeriod?.id || planningPeriods[0]?.id;
      if (periodId && typeof periodId === 'string') {
        setSelectedPlanningPeriod(periodId);
      }
    }
  }, [planningPeriods, selectedPlanningPeriod]);

  useEffect(() => {
    if (scenarios.length > 0 && !selectedScenario) {
      const baselineScenario = scenarios.find((s: any) => s.isBaseline);
      const scenarioId = baselineScenario?.id || scenarios[0]?.id;
      if (scenarioId && typeof scenarioId === 'string') {
        setSelectedScenario(scenarioId);
      }
    }
  }, [scenarios, selectedScenario]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      const params: { planningPeriodId: string; scenarioId?: string } = {
        planningPeriodId: selectedPlanningPeriod,
      };

      if (selectedScenario && selectedScenario !== '' && selectedScenario !== 'undefined') {
        params.scenarioId = selectedScenario;
      }

      dispatch(fetchExecutiveDashboard(params));
    }
  }, [dispatch, selectedPlanningPeriod, selectedScenario]);

  const planningPeriodOptions = [
    { value: '', label: 'Select Planning Period' },
    ...planningPeriods.map((period: any) => ({
      value: period.id || '',
      label: `${period.name || 'Unknown'}${period.isActive ? ' (Active)' : ''}`,
    })),
  ];

  const scenarioOptions = [
    { value: '', label: 'All Scenarios' },
    ...scenarios.map((scenario: any) => ({
      value: scenario.id || '',
      label: `${scenario.name || 'Unknown'}${scenario.isBaseline ? ' (Baseline)' : ''}`,
    })),
  ];

  const getOverallHealthStatus = () => {
    if (!executive.kpis) return 'unknown';
    
    const { q1Readiness, activeEpics, dependenciesAtRisk } = executive.kpis;
    
    // Calculate health score based on multiple factors
    let healthScore = 0;
    
    // Readiness factor (40% weight)
    if (q1Readiness.status === 'on_track') healthScore += 40;
    else if (q1Readiness.status === 'at_risk') healthScore += 20;
    
    // EPIC delivery factor (40% weight)
    const epicHealthRatio = activeEpics.total > 0 ? 
      (activeEpics.onTrack / activeEpics.total) : 0;
    healthScore += epicHealthRatio * 40;
    
    // Dependencies factor (20% weight)
    const dependencyRisk = dependenciesAtRisk.total > 0 ? 
      ((dependenciesAtRisk.critical + dependenciesAtRisk.high) / dependenciesAtRisk.total) : 0;
    healthScore += (1 - dependencyRisk) * 20;
    
    if (healthScore >= 80) return 'healthy';
    if (healthScore >= 60) return 'at_risk';
    return 'critical';
  };

  const getHealthBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <GlassBadge variant="success">Healthy</GlassBadge>;
      case 'at_risk':
        return <GlassBadge variant="warning">At Risk</GlassBadge>;
      case 'critical':
        return <GlassBadge variant="danger">Critical</GlassBadge>;
      default:
        return <GlassBadge variant="default">Unknown</GlassBadge>;
    }
  };

  const overallHealth = getOverallHealthStatus();

  const selectedPeriodName = planningPeriods.find((p: any) => p.id === selectedPlanningPeriod)?.name || '';

  return (
    <div className="min-h-screen p-6 lg:p-8 glass-page-bg">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <SectionReveal>
          <GlassPageHeader
            title="Executive Dashboard"
            subtitle={selectedPeriodName ? `Portfolio Performance Overview - ${selectedPeriodName}` : 'Portfolio Performance Overview'}
            icon={<BarChart3 className="w-6 h-6" />}
            iconGradient="blue-violet"
            actions={
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 mr-2">
                  <span className="text-sm text-gray-500">Portfolio Health:</span>
                  {getHealthBadge(overallHealth)}
                </div>
                <GlassSelect
                  value={selectedPlanningPeriod}
                  onChange={setSelectedPlanningPeriod}
                  options={planningPeriodOptions}
                  className="w-48"
                  size="sm"
                />
                <GlassSelect
                  value={selectedScenario}
                  onChange={setSelectedScenario}
                  options={scenarioOptions}
                  className="w-40"
                  size="sm"
                />
                <GlassButton variant="secondary" leftIcon={<Download className="w-4 h-4" />}>
                  Export
                </GlassButton>
                <GlassButton variant="gradient" leftIcon={<FileText className="w-4 h-4" />}>
                  Generate Report
                </GlassButton>
              </div>
            }
          />
        </SectionReveal>

        {/* KPI Cards */}
        <SectionReveal delay={0.1}>
          <KPICards
            kpis={executive.kpis}
            isLoading={isLoading}
          />
        </SectionReveal>

        {/* Main Content Grid - 3 column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Charts (2 columns) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Category Compliance */}
            <SectionReveal delay={0.15}>
              <CategoryComplianceChart
                compliance={executive.categoryCompliance}
                isLoading={isLoading}
              />
            </SectionReveal>

            {/* Quarterly Readiness */}
            <SectionReveal delay={0.2}>
              <QuarterlyReadinessChart
                readiness={executive.quarterlyReadiness}
                isLoading={isLoading}
              />
            </SectionReveal>

            {/* Investment by IS */}
            <SectionReveal delay={0.25}>
              <InvestmentByISChart
                investmentData={executive.investmentByIS}
                isLoading={isLoading}
              />
            </SectionReveal>
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-6">
            {/* EPIC Delivery Trend */}
            <SectionReveal delay={0.3}>
              <EpicDeliveryTrendChart
                deliveryTrend={executive.epicDeliveryTrend}
                isLoading={isLoading}
              />
            </SectionReveal>

            {/* Dependencies at Risk */}
            <SectionReveal delay={0.35}>
              <DependenciesAtRiskList
                dependencies={executive.dependenciesAtRisk}
                isLoading={isLoading}
              />
            </SectionReveal>

            {/* Portfolio Summary */}
            <SectionReveal delay={0.4}>
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Portfolio Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-accent-blue/10">
                    <div>
                      <p className="text-sm text-[var(--muted)]">Total Investment</p>
                      <p className="text-xl font-bold text-[var(--text)]">
                        {formatCurrencyCompact(executive.kpis?.totalInvestment?.dollars)}
                      </p>
                    </div>
                    {executive.kpis?.totalInvestment?.change !== undefined && (
                      <span className={`text-sm font-medium ${
                        executive.kpis.totalInvestment.change > 0 ? 'text-status-success' :
                        executive.kpis.totalInvestment.change < 0 ? 'text-status-error' : 'text-[var(--muted)]'
                      }`}>
                        {formatPercentTrend(executive.kpis.totalInvestment.change)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--accent)]/10">
                    <div>
                      <p className="text-sm text-[var(--muted)]">Active EPICs</p>
                      <p className="text-xl font-bold text-[var(--text)]">
                        {executive.kpis?.activeEpics?.total || 0}
                      </p>
                    </div>
                    <span className="text-sm text-[var(--muted)]">
                      {executive.kpis?.activeEpics?.onTrack || 0} on track
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-status-success/10">
                    <div>
                      <p className="text-sm text-[var(--muted)]">Q1 Readiness</p>
                      <p className="text-xl font-bold text-status-success">
                        {executive.kpis?.q1Readiness?.percentage || 0}%
                      </p>
                    </div>
                    <span className="text-sm text-[var(--muted)]">
                      {executive.kpis?.q1Readiness?.readyFeatures || 0}/{executive.kpis?.q1Readiness?.totalFeatures || 0}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-status-warning/10">
                    <div>
                      <p className="text-sm text-[var(--muted)]">Dependencies at Risk</p>
                      <p className="text-xl font-bold text-status-warning">
                        {executive.kpis?.dependenciesAtRisk?.total || 0}
                      </p>
                    </div>
                    <span className="text-sm text-[var(--muted)]">
                      {executive.kpis?.dependenciesAtRisk?.critical || 0} critical
                    </span>
                  </div>
                </div>
              </GlassCard>
            </SectionReveal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboardPage;